import { Fragment, useState, useMemo } from 'react';
import ChevronRightSmallSolid from '@simplywallst/ui-core/icons/ChevronRightSmallSolid';
import IntersectionObserver from '@researchgate/react-intersection-observer';
import {
  StyledTable,
  TableHeadRow,
  TableHeadCell,
  ScrollPane,
  CtaButton,
} from './styled';
import { useHoldingsLimits } from '@/hooks/portfolio/useHoldingsLimits';
import type { Holding, Portfolio } from '@/hooks/portfolio/usePortfolio';
import { HoldingRow } from './HoldingRow';

interface Props {
  portfolio: Portfolio;
}

const headers = [
  'Symbol',
  '+2y Payment \n& Contribution',
  'Yield vs \nYield on Cost',
  'Dividend Score \n& Growth',
];

export const DividendQualityForecastTable = ({ portfolio }: Props) => {
  const [isSticky, setIsSticky] = useState(false);

  const { holdings } = useHoldingsLimits({
    portfolioHoldings: portfolio.holdings,
  });

  const cellContents = useMemo(
    () =>
      headers.map((header) => {
        const cellLines = header.split('\n');
        return cellLines.map((line, index) => (
          <Fragment key={index}>
            {line}
            <br />
          </Fragment>
        ));
      }),
    []
  );

  const [firstHeadCell, ...restHeadCells] = cellContents;

  return (
    <ScrollPane>
      <StyledTable>
        <thead>
          <TableHeadRow>
            <IntersectionObserver
              threshold={[1]}
              onChange={({ intersectionRatio }) => {
                setIsSticky(intersectionRatio < 1);
              }}
            >
              <TableHeadCell isSticky={isSticky}>{firstHeadCell}</TableHeadCell>
            </IntersectionObserver>
            {restHeadCells.map((cell, index) => (
              <TableHeadCell key={index}>{cell}</TableHeadCell>
            ))}
          </TableHeadRow>
        </thead>
        <tbody>
          {holdings.map((holding: Holding) => {
            return (
              <HoldingRow
                key={holding.id}
                holding={holding}
                isSticky={isSticky}
                isAnalysisRestricted={portfolio.isAnalysisRestricted}
              />
            );
          })}
        </tbody>
      </StyledTable>
      <CtaButton
        marginTop
        as="a"
        href="/discover/investing-ideas/146/dividend-powerhouses-3-yield"
        styleType="passiveLarge"
      >
        Discover Top Quality Dividend Stocks
        <ChevronRightSmallSolid />
      </CtaButton>
    </ScrollPane>
  );
};
